import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Code, Smartphone, Zap, Play, Settings, Users } from 'lucide-react';
import SignupFormModal from '../components/SignupFormModal';

const WHATSAPP_URL = `https://wa.me/447757233260?text=${encodeURIComponent(
    `Hi! I just submitted the contact form on your website. Looking forward to hearing from the Qorvix team.`
)}`;

export default function AboutPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const [modalButtonType, setModalButtonType] = useState('demo');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        website: '',
        service: 'How can we help?',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const whyQorvixFeatures = [
        {
            title: "No-Code Simplicity",
            icon: "💜",
            color: "from-purple-100 to-purple-50"
        },
        {
            title: "Affordable Scalability",
            icon: "💙",
            color: "from-blue-100 to-blue-50"
        },
        {
            title: "Human + AI Collaboration",
            icon: "💗",
            color: "from-pink-100 to-pink-50"
        }
    ];

    const philosophyPoints = [
        {
            number: "01",
            title: "Eliminate manual busywork",
            color: "bg-blue-500"
        },
        {
            number: "02",
            title: "Unify your digital tools",
            color: "bg-orange-500"
        },
        {
            number: "03",
            title: "Automate growth operation",
            color: "bg-red-500"
        }
    ];

    const services = [
        {
            Icon: Zap,
            title: "Automation Strategy",
            description: "We analyze your current processes and design efficient, AI-powered workflows."
        },
        {
            Icon: Smartphone,
            title: "CRM & WhatsApp Integration",
            description: "Instantly connect Airtable, Make.com, and WhatsApp to sync leads and follow-ups automatically."
        },
        {
            Icon: Code,
            title: "Custom API Development",
            description: "When your tools can't talk to each other, we build custom bridges to make them smarter."
        }
    ];

    const stats = [
        {
            number: "500k",
            label: "Messages Automated monthly"
        },
        {
            number: "15K+",
            label: "Leads processed Daily"
        },
        {
            number: "200+",
            label: "UK Businesses Powered By QORVIX"
        }
    ];
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!agreed) {
            setSubmitError('Please agree to the Terms & Conditions');
            return;
        }

        if (!formData.name || !formData.email || !formData.message) {
            setSubmitError('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    service: formData.service,
                    message: `${formData.message}\n\nWebsite: ${formData.website || 'Not provided'}`,
                    timestamp: new Date().toISOString()
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSubmitSuccess(true);
                // Force redirect to WhatsApp — navigation (not popup) is never blocked
                setTimeout(() => {
                    window.location.href = WHATSAPP_URL;
                }, 1500);
            } else {
                setSubmitError(data.error || 'Failed to send message. Please try again.');
            }
        } catch (error) {
            console.error('Submit error:', error);
            setSubmitError('Failed to send message. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        
        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hover-lift {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }
      `}</style>

            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-2">
                            <img
                                src="/logo-qorvix.png"
                                alt="Qorvix Logo"
                                className="h-10 w-auto"
                            />
                        </Link>
                        {/* Desktop Menu */}
                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <div className="relative group">
                                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                                    Resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                {/* Dropdown Menu */}
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        <a href="/documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</a>
                                        <a href="/integrations" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Integration</a>
                                        <Link to="/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">FAQs</Link>
                                        <a href="/help-center" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Help center</a>
                                        <a href="/roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</a>
                                    </div>
                                </div>
                            </div>
                            <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="bg-transparent border-2 border-orange-500 text-orange-500 px-6 py-2.5 rounded-lg hover:bg-orange-500 hover:text-white transition-all font-medium text-[15px]">
                                Start free demo
                            </button>
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden text-gray-900"
                        >
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Modal - Add this after </nav> */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>

                    {/* Menu Panel */}
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        {/* Close Button */}
                        <button
                            onClick={() => setMobileMenuOpen(false)}
                            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X size={24} />
                        </button>

                        {/* Menu Items */}
                        <nav className="mt-4 space-y-6">
                            <Link
                                to="/"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Home
                            </Link>
                            <Link
                                to="/about"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                About
                            </Link>
                            <Link
                                to="/how-it-works"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                How it works
                            </Link>
                            <Link
                                to="/portfolio"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                portfolio
                            </Link>

                            {/* Resources Dropdown */}
                            <div>
                                <button className="w-full text-left text-lg font-medium text-gray-900 flex items-center gap-2">
                                    resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="mt-1">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="mt-4 ml-4 space-y-4">
                                    <Link
                                        to="/documentation"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Documentation
                                    </Link>
                                    <Link
                                        to="/integrations"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Integrations
                                    </Link>
                                    <Link
                                        to="/faq"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        FAQs
                                    </Link>
                                    <Link
                                        to="/help-center"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Help center
                                    </Link>
                                    <Link
                                        to="/roadmap"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Roadmap
                                    </Link>
                                </div>
                            </div>

                            <Link
                                to="/contact"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Contact us
                            </Link>

                            {/* CTA Button */}
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="text-lg font-medium text-orange-500 border-b-2 border-orange-500 pb-1">
                                Start free demo
                            </button>
                        </nav>
                    </div>
                </div>
            )}
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-4 overflow-hidden bg-gradient-to-br from-orange-100 via-orange-50 to-white">
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
                                We Help Businesses<br />
                                Work Smarter
                            </h1>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                Qorvix Helps Teams Automate Repetitive Work, Connect Customer Touchpoints, And Build Intelligent Systems That Scale — Without Code. From Lead Generation To Real-Time CRM Updates, Qorvix Gives Businesses Their Most Valuable Asset Back: Time.
                            </p>
                        </div>
                        <div>
                            <div className="relative bg-black rounded-3xl p-8 shadow-2xl">
                                <div className="bg-white rounded-2xl p-6 relative">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                                            <span className="text-white text-2xl font-bold">Q</span>
                                        </div>
                                        <h3 className="text-2xl font-bold">Leads</h3>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                            <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                                                <span className="text-sm">📊</span>
                                            </div>
                                            <span className="font-medium">Dashboard</span>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                                            <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center">
                                                <span className="text-sm">📝</span>
                                            </div>
                                            <span className="font-medium text-orange-600">Leads</span>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                            <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                                                <span className="text-sm">⚡</span>
                                            </div>
                                            <span className="font-medium">Auto-Replies</span>
                                        </div>
                                    </div>

                                    <div className="mt-6 bg-white border-2 border-gray-200 rounded-xl p-4">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white">
                                                👋
                                            </div>
                                            <div>
                                                <div className="font-bold">John Doe</div>
                                                <div className="text-sm text-gray-500">Toyota Camry 2022</div>
                                            </div>
                                        </div>
                                        <div className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full inline-block text-sm font-medium mb-2">
                                            NEW LEAD
                                        </div>
                                        <div className="text-sm text-gray-600 mb-2">
                                            Auto-replied in <span className="font-bold">2 seconds</span>
                                        </div>
                                        <p className="text-sm text-gray-700">
                                            Hi, I'm interested in the Camry. Is it still available?
                                        </p>
                                    </div>

                                    <div className="absolute -right-4 top-1/2 -translate-y-1/2">
                                        <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                                            <Play size={24} className="text-white ml-1" fill="white" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Qorvix Section */}
            <section className="py-20 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Qorvix</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Smarter automations. Seamless integrations. Real business impact.
                        </p>
                    </div>

                    <div className="flex justify-center items-center gap-8 mb-20">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                                <Zap className="text-white" size={28} />
                            </div>
                            <div className="w-3 h-3 bg-purple-300 rounded-full"></div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="w-3 h-3 bg-cyan-300 rounded-full"></div>
                            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                                <Settings className="text-white" size={28} />
                            </div>
                            <div className="w-3 h-3 bg-cyan-300 rounded-full"></div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="w-3 h-3 bg-pink-300 rounded-full"></div>
                            <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                                <Users className="text-white" size={28} />
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 text-center">
                        <div>
                            <h3 className="text-xl font-bold">No-Code Simplicity</h3>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold">Affordable Scalability</h3>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold">Human + AI Collaboration</h3>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Qorvix Philosophy */}
            <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-4xl md:text-5xl font-bold mb-6">
                                The <span className="text-gradient">Qorvix</span> Philosophy
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed mb-8">
                                We believe automation isn't about replacing people — it's about empowering them. Qorvix builds intelligent systems that amplify human efficiency, connect data across platforms, and create workflows that run 24/7.
                            </p>
                            <div className="space-y-4">
                                {philosophyPoints.map((point, index) => (
                                    <div key={index} className="flex items-center gap-4">
                                        <div className={`w-12 h-12 ${point.color} rounded-lg flex items-center justify-center text-white font-bold`}>
                                            {point.number}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg">{point.title}</h4>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="relative">
                                <img
                                    src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop"
                                    alt="AI Technology"
                                    className="rounded-3xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Services Section */}
            <section className="py-20 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&h=600&fit=crop"
                                alt="AI Robot"
                                className="rounded-3xl shadow-2xl hover-lift"
                            />
                        </div>
                        <div>
                            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our services</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                From lead capture to payment verification — Qorvix connects every part of your workflow using AI-driven automations.
                            </p>
                            <div className="space-y-6 mb-8">
                                {services.map((service, index) => {
                                    const IconComponent = service.Icon;
                                    return (
                                        <div key={index} className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <IconComponent className="text-orange-500" size={24} />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-lg mb-1">{service.title}</h4>
                                                <p className="text-gray-600 text-sm">{service.description}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <button className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-xl transition-all">
                                See More
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-12 text-center">
                        {stats.map((stat, index) => (
                            <div key={index}>
                                <div className="text-5xl md:text-6xl font-bold text-gradient mb-3">
                                    {stat.number}
                                </div>
                                <p className="text-gray-600 font-medium">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            {/* Featured Publications Section */}
            <section className="py-20 px-4 bg-gray-50">
                <div className="max-w-5xl mx-auto text-center">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        Featured <span className="text-gradient">Publications</span>
                    </h2>
                    <p className="text-lg text-gray-600 mb-12">
                        Our insights and expertise featured in leading industry publications
                    </p>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Publication 1 */}
                        <a
                            href="https://standardmirror.ng/qorvix-unveils-new-system-to-automate-and-streamline-customer-communication/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all group"
                        >
                            <div className="mb-6">
                                <img
                                    src="https://i.ibb.co/DH4sT201/publication-1-cover.jpg"
                                    alt="Publication 1"
                                    className="w-full h-64 object-cover rounded-lg"
                                />
                            </div>
                            <h3 className="text-2xl font-bold mb-3 group-hover:text-orange-500 transition-colors">
                                Qorvix Unveils New System to Automate and Streamline Customer Communication
                            </h3>
                            <p className="text-gray-600 mb-4">
                                With customers reaching out across multiple platforms at all hours, many organizations are struggling to maintain timely and consistent engagement. The growing volume of messages often leads to slow replies, overlooked follow-ups, inconsistent communication, and missed revenue opportunities.
                            </p>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">Standard Mirror</span>
                                <span className="text-orange-500 font-semibold group-hover:translate-x-2 transition-transform">
                                    Read Article →
                                </span>
                            </div>
                        </a>

                        {/* Publication 2 */}
                        <a
                            href="https://sunnews.ng/qorvix-launches-automation-platform-to-help-businesses-run-customer-conversations-as-a-system/
"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all group"
                        >
                            <div className="mb-6">
                                <img
                                    src="https://i.ibb.co/5gyzZGM8/publication-2-cover.jpg"
                                    alt="Publication 2"
                                    className="w-full h-64 object-cover rounded-lg"
                                />
                            </div>
                            <h3 className="text-2xl font-bold mb-3 group-hover:text-orange-500 transition-colors">
                                Qorvix Unveils New System to Automate and Streamline Customer Communication
                            </h3>
                            <p className="text-gray-600 mb-4">
                                Qorvix has entered the market with a platform designed to help businesses transform customer communication into an organized, scalable system instead of relying on scattered manual responses.
                            </p>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">SunNews</span>
                                <span className="text-orange-500 font-semibold group-hover:translate-x-2 transition-transform">
                                    Read Article →
                                </span>
                            </div>
                        </a>
                    </div>
                </div>
            </section>
            {/* Contact Form Section */}
            <section className="py-20 px-4 bg-white">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">Ready To Start A Projects</h2>
                        <p className="text-lg text-gray-600">
                            We will contact again after receive your request in 24h
                        </p>
                        <p className="text-sm text-red-500 mt-2">This field is required as mark as *</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {submitSuccess && (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                                ✅ Message sent successfully! We'll get back to you within 24 hours.
                            </div>
                        )}

                        {submitError && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                                ❌ {submitError}
                            </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-6">
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Name*"
                                required
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                            />
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Enter your email address*"
                                required
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                            />
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="Phone number (Optional)"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                            />
                            <input
                                type="text"
                                name="website"
                                value={formData.website}
                                onChange={handleInputChange}
                                onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value && !value.match(/^https?:\/\//i)) {
                                        setFormData({
                                            ...formData,
                                            website: `https://${value}`
                                        });
                                    }
                                }}
                                placeholder="Your website (Optional)"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                            />
                        </div>

                        <select
                            name="service"
                            value={formData.service}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                        >
                            <option>How can we help?</option>
                            <option>Automation Strategy</option>
                            <option>CRM Integration</option>
                            <option>Custom API Development</option>
                            <option>Lead Management System</option>
                            <option>WhatsApp Automation</option>
                            <option>Other</option>
                        </select>

                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="How can we help you?*"
                            rows="5"
                            required
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                        ></textarea>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                                className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                            />
                            <label htmlFor="terms" className="text-sm text-gray-600">
                                I agree to the Terms & conditions.*
                            </label>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-lg text-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Sending...' : 'Send Your Request'}
                        </button>
                    </form>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="text-gradient">Qorvix</span> — Automate What Matters
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems. From CRM integrations to WhatsApp automations — we build smart, no-code solutions that work.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                                <Link to="/how-it-works" className="block text-gray-600 hover:text-gray-900 transition-colors">How it works</Link>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Resources</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/documentation" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    Documentation
                                </Link>

                                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    Integration
                                </Link>

                                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    FAQs
                                </Link>

                                <Link to="/help-center" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    Help Center
                                </Link>

                                <Link to="/roadmap" className="block text-gray-600 hover:text-gray-900 transition-colors">
                                    Roadmap
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">
                            © Qorvix Technologies. All Rights Reserved. Built with purpose and precision by <span className="font-semibold">Qorvix Team</span>.
                        </div>
                    </div>
                </div>
            </footer>
            {/* Signup Modal */}
            <SignupFormModal
                isOpen={isSignupModalOpen}
                onClose={() => setIsSignupModalOpen(false)}
                buttonType={modalButtonType}
            />
        </div>
    );
}